import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { Plus, Users, Search, LogOut as ExitIcon, FileArchive } from "lucide-react";

const STATUS_STYLES = {
  active: "bg-[hsl(148_40%_88%)] text-[hsl(var(--forest))]",
  "on-notice": "bg-[hsl(var(--brass)/0.2)] text-[hsl(var(--brass-2))]",
  probation: "bg-[hsl(var(--bg-2))] text-[hsl(var(--ink-soft))]",
  exited: "bg-[hsl(var(--rule))] text-[hsl(var(--ink-soft))]",
};

const emptyForm = {
  name: "", role_title: "", department: "", phone: "", email: "",
  employment_type: "full-time", joining_date: "", ctc: "",
};

export default function StaffEmployees() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [forbidden, setForbidden] = useState(false);
  const [filter, setFilter] = useState("active");
  const [q, setQ] = useState("");
  const [creating, setCreating] = useState(false);
  const [form, setForm] = useState(emptyForm);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/hr/employees");
      setItems(data);
      setForbidden(false);
    } catch (err) {
      if (err?.response?.status === 403) {
        setForbidden(true);
      } else {
        const d = err?.response?.data?.detail;
        toast.error(typeof d === "string" ? d : "Could not load employees.");
      }
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  const filtered = useMemo(() => {
    return items.filter((e) => {
      if (filter !== "all" && e.status !== filter) return false;
      if (q && !`${e.name} ${e.role_title} ${e.department} ${e.email} ${e.phone}`.toLowerCase().includes(q.toLowerCase())) return false;
      return true;
    });
  }, [items, filter, q]);

  const create = async (e) => {
    e.preventDefault();
    try {
      const payload = { ...form, ctc: form.ctc === "" ? null : Number(form.ctc) };
      await api.post("/hr/employees", payload);
      toast.success("Employee added");
      setForm(emptyForm);
      setCreating(false);
      load();
    } catch (err) {
      const d = err?.response?.data?.detail;
      toast.error(typeof d === "string" ? d : "Create failed");
    }
  };

  const counts = useMemo(() => {
    const c = { all: items.length, active: 0, "on-notice": 0, probation: 0, exited: 0 };
    items.forEach((e) => { c[e.status] = (c[e.status] || 0) + 1; });
    return c;
  }, [items]);

  return (
    <div data-testid="staff-employees">
      {forbidden ? (
        <div data-testid="emp-forbidden" className="mt-4 p-10 border border-[hsl(var(--rule))] bg-[hsl(var(--bg-2))] text-center">
          <Users size={28} className="mx-auto text-[hsl(var(--brass))]" />
          <h2 className="mt-4 text-2xl">HR is admin & manager only</h2>
          <p className="mt-2 text-sm text-[hsl(var(--ink-soft))] max-w-md mx-auto">
            You don&apos;t have access to Employee records. Ask an admin to promote your role if you need to view or manage HR data.
          </p>
        </div>
      ) : (
      <>
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <div className="eyebrow">HR</div>
          <h1 className="mt-2 text-4xl">Employees</h1>
          <p className="mt-3 text-[hsl(var(--ink-soft))] max-w-xl">
            Onboard new hires, track performance reviews, and manage resignation + full-and-final
            handover.
          </p>
        </div>
        <div className="flex gap-2 flex-wrap">
          <button data-testid="emp-export-zip" onClick={async () => {
            try {
              const res = await api.get("/hr/employees/export.zip", { responseType: "blob" });
              const url = URL.createObjectURL(res.data);
              const a = document.createElement("a");
              a.href = url;
              a.download = `TBB-Employees-${new Date().toISOString().slice(0,10)}.zip`;
              document.body.appendChild(a); a.click(); a.remove();
              setTimeout(() => URL.revokeObjectURL(url), 1000);
            } catch (err) {
              console.error("Export failed:", err);
              toast.error("Export failed");
            }
          }} className="inline-flex items-center gap-2 px-4 py-2.5 text-sm border border-[hsl(var(--brass))] rounded text-[hsl(var(--brass-2))] hover:bg-[hsl(var(--brass))] hover:text-[hsl(var(--bg))] transition-colors">
            <FileArchive size={14} /> Export all (ZIP)
          </button>
          <button data-testid="emp-new-btn" onClick={() => setCreating((v) => !v)} className="btn-brass !py-2.5 !px-5 text-sm">
            <Plus size={14} /> {creating ? "Cancel" : "Onboard new"}
          </button>
        </div>
      </div>

      {creating && (
        <form data-testid="emp-new-form" onSubmit={create} className="mt-6 border border-[hsl(var(--brass))] bg-[hsl(var(--bg-2))] p-6 grid grid-cols-1 md:grid-cols-6 gap-3">
          <input required placeholder="Full name" data-testid="new-emp-name" className="emp-inp md:col-span-2" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />
          <input placeholder="Role (e.g., Sous chef)" data-testid="new-emp-role" className="emp-inp md:col-span-2" value={form.role_title} onChange={(e) => setForm({ ...form, role_title: e.target.value })} />
          <input placeholder="Department" data-testid="new-emp-dept" className="emp-inp md:col-span-2" value={form.department} onChange={(e) => setForm({ ...form, department: e.target.value })} />
          <input placeholder="Phone" data-testid="new-emp-phone" className="emp-inp md:col-span-2" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} />
          <input placeholder="Email" type="email" data-testid="new-emp-email" className="emp-inp md:col-span-2" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} />
          <select className="emp-inp md:col-span-2" value={form.employment_type} onChange={(e) => setForm({ ...form, employment_type: e.target.value })}>
            <option value="full-time">Full-time</option>
            <option value="part-time">Part-time</option>
            <option value="contract">Contract</option>
          </select>
          <input type="date" placeholder="Joining date" data-testid="new-emp-joining" className="emp-inp md:col-span-2" value={form.joining_date} onChange={(e) => setForm({ ...form, joining_date: e.target.value })} />
          <input type="number" placeholder="CTC ₹/yr" data-testid="new-emp-ctc" className="emp-inp md:col-span-2" value={form.ctc} onChange={(e) => setForm({ ...form, ctc: e.target.value })} />
          <div className="md:col-span-6">
            <button type="submit" data-testid="new-emp-submit" className="btn-brass">Add employee</button>
            <span className="ml-3 text-xs text-[hsl(var(--ink-soft))]">More details can be filled on the profile after creation.</span>
          </div>
        </form>
      )}

      {/* Filters + search */}
      <div className="mt-8 flex flex-wrap items-center gap-3">
        <div className="inline-flex border border-[hsl(var(--rule))] rounded-full p-1 bg-[hsl(var(--bg-2))]">
          {["all", "active", "on-notice", "probation", "exited"].map((s) => (
            <button
              key={s}
              data-testid={`emp-filter-${s}`}
              onClick={() => setFilter(s)}
              className={`px-4 py-1.5 text-xs uppercase tracking-wider rounded-full transition-all ${filter === s ? "bg-[hsl(var(--forest))] text-[hsl(var(--bg))]" : "text-[hsl(var(--ink-soft))]"}`}
            >
              {s === "all" ? "All" : s} <span className="opacity-70">· {counts[s] ?? 0}</span>
            </button>
          ))}
        </div>
        <div className="flex-1 min-w-[240px] relative">
          <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-[hsl(var(--ink-soft))]" />
          <input
            data-testid="emp-search"
            placeholder="Search name, role, phone, email..."
            value={q}
            onChange={(e) => setQ(e.target.value)}
            className="w-full pl-9 pr-4 py-2 border border-[hsl(var(--rule))] rounded bg-[hsl(var(--bg-2))] text-sm focus:outline-none focus:border-[hsl(var(--forest))]"
          />
        </div>
      </div>

      {loading && <div className="mt-8 text-[hsl(var(--ink-soft))]">Loading...</div>}

      {!loading && filtered.length === 0 && (
        <div data-testid="emp-empty" className="mt-8 p-10 border border-[hsl(var(--rule))] bg-[hsl(var(--bg-2))] text-center text-[hsl(var(--ink-soft))]">
          <Users size={22} className="mx-auto text-[hsl(var(--brass))]" />
          <div className="mt-3">No employees match. Onboard your first one from the button above.</div>
        </div>
      )}

      {!loading && filtered.length > 0 && (
        <div className="mt-6 border border-[hsl(var(--rule))]">
          <table className="w-full text-sm">
            <thead className="bg-[hsl(var(--bg-2))] text-left text-[10px] uppercase tracking-widest text-[hsl(var(--ink-soft))]">
              <tr>
                <th className="p-3">Name</th>
                <th className="p-3">Role</th>
                <th className="p-3">Contact</th>
                <th className="p-3">Joined</th>
                <th className="p-3">Type</th>
                <th className="p-3">Status</th>
                <th className="p-3 text-right"></th>
              </tr>
            </thead>
            <tbody className="divide-y divide-[hsl(var(--rule))] bg-[hsl(var(--bg-2))]">
              {filtered.map((e) => (
                <tr key={e.id} data-testid={`emp-row-${e.id}`}>
                  <td className="p-3">
                    <div className="flex items-center gap-3">
                      {e.photo_url ? (
                        <img src={e.photo_url} alt="" className="w-8 h-8 rounded-full object-cover" />
                      ) : (
                        <div className="w-8 h-8 rounded-full bg-[hsl(var(--rule))] flex items-center justify-center text-xs font-semibold text-[hsl(var(--ink-soft))]">
                          {(e.name || "?").slice(0, 1).toUpperCase()}
                        </div>
                      )}
                      <div>
                        <div className="font-semibold">{e.name}</div>
                        {e.department && <div className="text-xs text-[hsl(var(--ink-soft))]">{e.department}</div>}
                      </div>
                    </div>
                  </td>
                  <td className="p-3">{e.role_title || "—"}</td>
                  <td className="p-3 text-xs text-[hsl(var(--ink-soft))]">
                    {e.phone && <div>{e.phone}</div>}
                    {e.email && <div>{e.email}</div>}
                  </td>
                  <td className="p-3 text-[hsl(var(--ink-soft))]">{e.joining_date || "—"}</td>
                  <td className="p-3 text-[hsl(var(--ink-soft))] uppercase text-[10px] tracking-wider">{e.employment_type}</td>
                  <td className="p-3">
                    <span className={`text-[10px] uppercase tracking-wider px-2 py-0.5 rounded ${STATUS_STYLES[e.status] || ""}`}>
                      {e.status}
                    </span>
                  </td>
                  <td className="p-3 text-right">
                    <Link to={`/staff/employees/${e.id}`} data-testid={`emp-open-${e.id}`} className="text-xs uppercase tracking-wider text-[hsl(var(--forest))] font-semibold">
                      Open →
                    </Link>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      </>
      )}

      <style>{`
        .emp-inp {
          background: hsl(var(--bg));
          border: 1px solid hsl(var(--rule));
          border-radius: 4px;
          padding: 0.6rem 0.8rem;
          font-family: inherit;
          font-size: 0.9rem;
          color: hsl(var(--ink));
        }
        .emp-inp:focus { outline: none; border-color: hsl(var(--forest)); }
      `}</style>
    </div>
  );
}
